import DynamicFormInput from "components/form/form";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { useEffect, useState, useCallback } from "react";
import { LoadingIcon } from "utils/constant";
import personImg from "../../../images/person.png";
import { 
  getLegislativeDraftingMappedSummary, 
  updateLegislativeDrafting 
} from "services/legislativeService";
import SingleLegalDraftingPaper from "./fragments/singleLegalDraftingPapers";
import { LegalDrafting } from "types"; 
import SingleLegalDraftingPapersDetail from "./fragments/singleLegalDraftingPaperDetails";
import FileViewer from "components/file/fileViewContainer";
import Pagination from "components/Pagination";
import BroadcastCard from "pages/conductLegalResearch/fragments/broadcastCard";
import { useTranslation } from "react-i18next";

export default function LegalDraftingPapers() {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeResearch, setResearch] = useState<LegalDrafting>();
  const [submitting, setSubmitting] = useState(false);
  const [broadcastLoading, setBroadcastLoading] = useState(false);
  const { token, user } = useAuth();
  const { showToast } = useToast();
  const [data, setData] = useState<{
    isLoading: boolean;
    data: LegalDrafting[];
  }>({ isLoading: true, data: [] });

  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{
    name: string;
    size: number;
    url: string;
    isFetched: boolean;
    type: string;
    uuid: string;
  } | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 2
  const fileRowProps = {
    fileViewerNumber: 2,
    setFileViewer2: setIsFileViewerOpen,
    setSelectedApplicantDocument: (document: any) => {
      setSelectedDocument(document);
      setIsFileViewerOpen(true);
    },
  };

  // Filter data based on search term
  const filteredData = useCallback(() => {
    if (!searchTerm.trim()) {
      return data.data || [];
    }

    const searchLower = searchTerm.toLowerCase().trim();
    return (data.data || []).filter((req: LegalDrafting) => {
      return (
        (req.letter_reference_number || "").toLowerCase().includes(searchLower) ||
        (req.organization_name || "").toLowerCase().includes(searchLower) ||
        (req.draft_summary || "").toLowerCase().includes(searchLower) ||
        (req.draft_instruction_summary || "").toLowerCase().includes(searchLower)
      );
    });
  }, [data.data, searchTerm]);

  // Paginate filtered data
  const paginatedData = useCallback(() => {
    const filtered = filteredData();
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filtered.slice(startIndex, endIndex);
  }, [filteredData, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredData().length / pageSize);

  // Update active research when paginated data changes
  useEffect(() => {
    const currentPaginated = paginatedData();
    if (currentPaginated.length > 0) {
      const currentActiveInPaginated = currentPaginated.find(
        item => item.id === activeResearch?.id
      );

      if (currentActiveInPaginated) {
        setResearch(currentActiveInPaginated);
      } else {
        setResearch(currentPaginated[0]);
      }
    } else {
      setResearch(undefined);
    }
  }, [paginatedData, activeResearch?.id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleSearch = async () => {
    setSubmitting(true);
    try {
      // Frontend search is handled by filteredData
    } catch (error) {
      console.error("Search error:", error);
      showToast(t("SearchFailed"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBroadcast = async () => {
    if (!activeResearch) return;
    setBroadcastLoading(true);
    try {
      const data = new FormData();
      data.append("is_published", (!activeResearch.is_published).toString());
      await updateLegislativeDrafting(activeResearch.id.toString(), data);
      const fetchedData = await getLegislativeDraftingMappedSummary();
      setData({
        isLoading: false,
        data: (fetchedData || []).filter((req: LegalDrafting) => req.is_published === true),
      });
      showToast(t("BroadcastStatusUpdated"), "success");
    } catch (error) {
      console.error("Error updating broadcast status:", error);
      showToast(t("FailedToUpdateBroadcastStatus"), "error");
    } finally {
      setBroadcastLoading(false);
    }
  };

  useEffect(() => {
    getLegislativeDraftingMappedSummary()
      .then((fetchedData: LegalDrafting[]) => {
        const publishedData = (fetchedData || []).filter((req: LegalDrafting) => req.is_published === true);
        setData({
          isLoading: false,
          data: publishedData,
        });

        if (publishedData.length > 0) {
          setResearch(publishedData[0]);
        }
        setCurrentPage(1);
      })
      .catch((error: any) => {
        console.error("Error fetching legislative draft requests:", error);
        showToast(t("FailedToFetchLegalDraftingPapers"), "error");
        setData((prev) => ({
          ...prev,
          isLoading: false,
        }));
      });
  }, [showToast, t]);

  // Handle Enter key for search
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Calculate filtered and paginated results
  const currentFilteredResults = filteredData();
  const currentPaginatedResults = paginatedData();
  const hasFilteredResults = currentFilteredResults.length > 0;

  return (
    <div className="flex flex-col md:flex-row h-full gap-4">
      <div className="flex flex-col flex-1 overflow-y-hidden">
        <div className="flex flex-col bg-white rounded-lg shadow-md p-4 md:p-6 mb-4">
          <p className="mb-2 font-medium">{t("search_drafting_papers_title")}</p>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="w-full">
              <div onKeyDown={handleKeyDown}>
                <DynamicFormInput
                  inputs={[
                    {
                      value: searchTerm,
                      type: "text",
                      label: t("search_drafting_papers_label"),
                      name: "letter_reference_number",
                      onChange: handleChange,
                      placeholder: t("search_drafting_papers_placeholder"),
                    },
                  ]}
                />
              </div>
            </div>
            <button
              onClick={handleSearch}
              disabled={submitting}
              className="bg-[#212121] hover:bg-[#141414] disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold py-2 px-12 rounded-md h-fit w-full md:w-fit"
            >
              {submitting ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon} {t("searching")}
                </div>
              ) : (
                t("search_button")
              )}
            </button>
          </div>
          {searchTerm && (
            <div className="mt-2 text-sm text-gray-600">
              {t("results_count", { count: currentFilteredResults.length, term: searchTerm })}
            </div>
          )}
        </div>

        {data.isLoading ? (
          <div className="text-center mb-auto text-gray-500 py-8">
            <div className="flex justify-center mb-2">
              {LoadingIcon}
            </div>
            <p>{t("loading_drafting_data")}</p>
          </div>
        ) : !hasFilteredResults ? (
          <div className="text-center mb-auto text-gray-500 py-8">
            <p>
              {searchTerm.trim()
                ? t("no_results_found", { term: searchTerm })
                : t("no_legal_drafting_papers_yet")}
            </p>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-4 overflow-y-scroll scrollbar-hide flex-1">
              {currentPaginatedResults.map((research: LegalDrafting) => (
                <SingleLegalDraftingPaper
                  key={research.id}
                  personImg={personImg}
                  research={research}
                  isActive={research.id === activeResearch?.id}
                  onClick={() => setResearch(research)}
                />
              ))}
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </div>

      {/* Detail Panel */}
      <div className="flex flex-col flex-1 h-full overflow-scroll scrollbar-hide">
        {hasFilteredResults && activeResearch ? (
          <>
            <SingleLegalDraftingPapersDetail
              personImg={personImg}
              research={activeResearch}
              fileRowProps={fileRowProps}
            />
            {user.roles.includes("LRDIFUDDirector") && (
              <div className="mb-4">
                <BroadcastCard
                  onClick={handleBroadcast}
                  isPublished={activeResearch.is_published}
                  title={t("broadcast_draft_legislation_title")}
                  description={t("broadcast_draft_legislation_description")}
                  additionalInfo={t("broadcast_draft_legislation_additional_info")}
                />
              </div>
            )}
          </>
        ) : hasFilteredResults && !activeResearch ? (
          <div className="text-center p-8 text-gray-500">
            <p>{t("select_drafting_paper_to_view")}</p>
          </div>
        ) : null}
      </div>

      <FileViewer
        selectedApplicantDocument={selectedDocument || undefined}
        isPopupOpen={isFileViewerOpen}
        setIsPopupOpen={setIsFileViewerOpen}
      />
    </div>
  );
}